// `.hashquill/state.json` (SPEC §4). Unpushed work lives here, in the user's folder,
// so it survives uninstalling the app or clearing site data. Never committed.
import type { Folder } from './folder';
import { DEFAULT_ENCODING, type TextEncodingInfo } from './normalize';

export const STATE_PATH = '.hashquill/state.json';

export type FileEntry = {
  /** GitHub blob SHA of the last pulled version. */
  blobSha: string;
  /**
   * Git blob SHA of the normalized text we wrote locally. Equals blobSha unless the
   * remote file had CRLF/BOM; comparing against it avoids phantom "edited" status.
   */
  localSha: string;
  /** Line endings and BOM of the file on GitHub, restored on push (normalized locally). */
  eol: TextEncodingInfo['eol'];
  bom: boolean;
};

export type LocalChange =
  | { path: string; op: 'modify' | 'add'; content: string }
  | { path: string; op: 'delete' };

export type LocalCommit = { id: string; message: string; createdAt: string; changes: LocalChange[] };

export type WorkState = {
  version: 1;
  repo: string;
  branch: string;
  prNumber: number | null;
  remoteHeadSha: string;
  remoteTreeSha: string;
  files: Record<string, FileEntry>;
  localCommits: LocalCommit[];
  /**
   * Files the last pull couldn't update because they changed on both sides. While this is
   * non-empty, remoteHeadSha stays at the old base and push is blocked (conflicts: M7).
   */
  needsResolving: string[];
};

export class StateFileError extends Error {
  constructor(message: string) { super(message); this.name = 'StateFileError'; }
}

export function emptyState(repo: string, branch = 'main'): WorkState {
  return { version: 1, repo, branch, prNumber: null, remoteHeadSha: '', remoteTreeSha: '', files: {}, localCommits: [], needsResolving: [] };
}

const isSha = (v: unknown) => typeof v === 'string' && /^[0-9a-f]{40}$/.test(v);
const isShaOrEmpty = (v: unknown) => v === '' || isSha(v);

/** Validate untrusted JSON from disk. Throws StateFileError with a reason. */
export function parseState(json: string): WorkState {
  let raw: unknown;
  try { raw = JSON.parse(json); } catch { throw new StateFileError('state.json is not valid JSON'); }
  const s = raw as Partial<WorkState>;
  if (!s || typeof s !== 'object') throw new StateFileError('state.json is not an object');
  if (s.version !== 1) throw new StateFileError(`Unsupported state version ${String(s.version)}`);
  if (typeof s.repo !== 'string' || !s.repo.includes('/')) throw new StateFileError('state.json has no repo');
  if (typeof s.branch !== 'string' || !s.branch) throw new StateFileError('state.json has no branch');
  if (!(s.prNumber === null || Number.isInteger(s.prNumber))) throw new StateFileError('Bad prNumber');
  if (!isShaOrEmpty(s.remoteHeadSha) || !isShaOrEmpty(s.remoteTreeSha)) throw new StateFileError('Bad remote SHAs');
  if (!s.files || typeof s.files !== 'object') throw new StateFileError('Bad files');
  for (const [p, e] of Object.entries(s.files)) {
    if (!e || !isSha(e.blobSha) || !isSha(e.localSha)) throw new StateFileError(`Bad file entry for ${p}`);
    // Older state files had no encoding info: those files were downloaded as-is (LF, no BOM).
    if (e.eol !== 'lf' && e.eol !== 'crlf') e.eol = DEFAULT_ENCODING.eol;
    if (typeof e.bom !== 'boolean') e.bom = DEFAULT_ENCODING.bom;
  }
  if (!Array.isArray(s.localCommits)) throw new StateFileError('Bad localCommits');
  for (const c of s.localCommits) {
    if (!c || typeof c.id !== 'string' || typeof c.message !== 'string' || !Array.isArray(c.changes)) {
      throw new StateFileError('Bad local commit');
    }
    for (const ch of c.changes) {
      const ok = ch && typeof ch.path === 'string' &&
        (ch.op === 'delete' || ((ch.op === 'modify' || ch.op === 'add') && typeof (ch as { content?: unknown }).content === 'string'));
      if (!ok) throw new StateFileError('Bad change in local commit');
    }
  }
  if (s.needsResolving === undefined) s.needsResolving = [];
  if (!Array.isArray(s.needsResolving) || !s.needsResolving.every((p) => typeof p === 'string')) throw new StateFileError('Bad needsResolving');
  return s as WorkState;
}

export async function readState(folder: Folder): Promise<WorkState | null> {
  const text = await folder.readText(STATE_PATH);
  return text === null ? null : parseState(text);
}

/**
 * Atomic: createWritable() writes to a swap file and replaces state.json on close(),
 * so a crash leaves either the old or the new file, never half of one (SPEC §4).
 * Only the window holding the folder lock may call this.
 */
export async function writeState(folder: Folder, state: WorkState): Promise<void> {
  await folder.writeText(STATE_PATH, `${JSON.stringify(state, null, 2)}\n`);
}

// ---------- Which folders we accept (SPEC §4) ----------

export type FolderCheck =
  | { kind: 'empty' }
  | { kind: 'ours'; state: WorkState }
  | { kind: 'not-empty' }
  | { kind: 'other-repo'; repo: string }
  | { kind: 'damaged'; reason: string };

/** Accept only an empty folder, or one whose state.json belongs to this repo. */
export async function checkFolder(folder: Folder, repo: string): Promise<FolderCheck> {
  let state: WorkState | null;
  try {
    state = await readState(folder);
  } catch (e) {
    if (e instanceof StateFileError) return { kind: 'damaged', reason: e.message };
    throw e;
  }
  if (state) return state.repo === repo ? { kind: 'ours', state } : { kind: 'other-repo', repo: state.repo };
  return (await folder.isEmpty()) ? { kind: 'empty' } : { kind: 'not-empty' };
}
